<template>
  <div class="property-right-registration-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('propertyRightRegistration.page.title')" :subtitle="$t('propertyRightRegistration.page.subtitle')" />

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.roomId" clearable class="w-full"
            :placeholder="$t('propertyRightRegistration.search.roomId')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.allNum" clearable class="w-full"
            :placeholder="$t('propertyRightRegistration.search.allNum')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.name" clearable class="w-full"
            :placeholder="$t('propertyRightRegistration.search.name')" @keyup.enter.native="handleSearch" />
        </el-col>
      </el-row>

      <!-- 更多查询条件 -->
      <div v-show="showMoreCondition">
        <el-row :gutter="16" class="ds-more-row">
          <el-col :xs="24" :sm="12" :md="8" :lg="6">
            <el-input v-model.trim="searchForm.link" clearable class="w-full"
              :placeholder="$t('propertyRightRegistration.search.link')" @keyup.enter.native="handleSearch" />
          </el-col>
          <el-col :xs="24" :sm="12" :md="8" :lg="6">
            <el-input v-model.trim="searchForm.idCard" clearable class="w-full"
              :placeholder="$t('propertyRightRegistration.search.idCard')" @keyup.enter.native="handleSearch" />
          </el-col>
          <el-col :xs="24" :sm="12" :md="8" :lg="6">
            <el-input v-model.trim="searchForm.address" clearable class="w-full"
              :placeholder="$t('propertyRightRegistration.search.address')" @keyup.enter.native="handleSearch" />
          </el-col>
          <el-col :xs="24" :sm="12" :md="8" :lg="6">
            <el-select v-model="searchForm.state" clearable class="w-full"
              :placeholder="$t('propertyRightRegistration.search.state')">
              <el-option v-for="item in states" :key="item.statusCd" :label="item.name" :value="item.statusCd" />
            </el-select>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8" :lg="6">
            <el-select v-model="searchForm.floorId" clearable class="w-full"
              :placeholder="$t('propertyRightRegistration.search.floor')" @change="handleFloorChange">
              <el-option v-for="item in floors" :key="item.floorId" :label="item.floorName" :value="item.floorId" />
            </el-select>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8" :lg="6">
            <el-select v-model="searchForm.unitId" clearable class="w-full"
              :placeholder="$t('propertyRightRegistration.search.unit')">
              <el-option v-for="item in units" :key="item.unitId"
                :label="`${item.unitNum}${$t('propertyRightRegistration.search.unit')}`" :value="item.unitId" />
            </el-select>
          </el-col>
        </el-row>
      </div>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="handleReset">
          {{ $t('common.reset') }}
        </el-button>
        <el-button type="text" icon="el-icon-more" @click="toggleMoreCondition">
          {{ showMoreCondition ? $t('common.hide') : $t('common.more') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="getList">
        <template #left>
          <span class="ds-panel__title">{{ $t('propertyRightRegistration.list.title') }}</span>
        </template>
        <template #right>
          <el-button type="primary" size="mini" icon="el-icon-plus" @click="handleAdd">
            {{ $t('common.add') }}
          </el-button>
        </template>
      </ds-toolbar>

      <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('propertyRightRegistration.page.empty')" @retry="getList">
        <el-table-column prop="prrId" :label="$t('propertyRightRegistration.table.prrId')" align="center" width="150">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.prrId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="roomId" :label="$t('propertyRightRegistration.table.roomId')" align="center" width="150">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.roomId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('propertyRightRegistration.table.roomNum')" align="center" width="150">
          <template slot-scope="scope">
            <span class="ds-num">
              {{ `${scope.row.floorNum}-${scope.row.unitNum}-${scope.row.roomNum}` }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="name" :label="$t('propertyRightRegistration.table.name')" align="center" width="110" />
        <el-table-column prop="link" :label="$t('propertyRightRegistration.table.link')" align="center" width="130">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.link || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="idCard" :label="$t('propertyRightRegistration.table.idCard')" align="center" min-width="160"
          show-overflow-tooltip />
        <el-table-column prop="address" :label="$t('propertyRightRegistration.table.address')" align="left" min-width="160"
          show-overflow-tooltip />
        <!-- 状态 Tag：颜色仅作辅助，始终带文字 -->
        <el-table-column prop="stateName" :label="$t('propertyRightRegistration.table.state')" align="center" width="120">
          <template slot-scope="scope">
            <ds-status-tag :type="resolveState(scope.row)" :label="scope.row.stateName || '-'" />
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" width="220" fixed="right">
          <template slot-scope="scope">
            <el-button v-if="scope.row.state !== '1'" size="mini" type="text" @click="handleExamine(scope.row)">
              {{ $t('common.examine') }}
            </el-button>
            <el-button size="mini" type="text" @click="handleEdit(scope.row)">
              {{ $t('common.edit') }}
            </el-button>
            <el-button size="mini" type="text" @click="handleDetail(scope.row)">
              {{ $t('common.detail') }}
            </el-button>
            <el-button size="mini" type="text" class="ds-btn-danger" @click="handleDelete(scope.row)">
              {{ $t('common.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange">
        <template #summary>
          <span class="ds-tip-text">{{ $t('propertyRightRegistration.page.tip') }}</span>
        </template>
      </ds-pagination-bar>
    </div>

    <!-- 子组件 -->
    <add-property-right-registration ref="addForm" @success="handleSuccess" />
    <examine-property-right-registration ref="examineForm" @success="handleSuccess" />
    <edit-property-right-registration ref="editForm" @success="handleSuccess" />
    <delete-property-right-registration ref="deleteForm" @success="handleSuccess" />
  </div>
</template>

<script>
import { getDict } from '@/api/community/communityApi'
import { getCommunityId } from '@/api/community/communityApi'
import { listPropertyRightRegistration } from '@/api/room/propertyRightRegistrationManageApi'
import { getFloors, getUnits } from '@/api/room/roomApi'
import AddPropertyRightRegistration from '@/components/room/addPropertyRightRegistration'
import ExaminePropertyRightRegistration from '@/components/room/examinePropertyRightRegistration'
import EditPropertyRightRegistration from '@/components/room/editPropertyRightRegistration'
import DeletePropertyRightRegistration from '@/components/room/deletePropertyRightRegistration'
import { resolveStatus } from '@/utils/statusMap'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'PropertyRightRegistrationManageList',
  components: {
    AddPropertyRightRegistration,
    ExaminePropertyRightRegistration,
    EditPropertyRightRegistration,
    DeletePropertyRightRegistration,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      showMoreCondition: false,
      searchForm: {
        roomId: '',
        allNum: '',
        name: '',
        link: '',
        idCard: '',
        address: '',
        state: '',
        floorId: '',
        unitId: '',
        communityId: ''
      },
      tableData: [],
      pagination: {
        current: 1,
        size: 10,
        total: 0
      },
      states: [],
      floors: [],
      units: []
    }
  },
  created() {
    this.searchForm.communityId = getCommunityId()
    this.getList()
    this.getDictData()
    this.getFloors()
  },
  methods: {
    async getList() {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          ...this.searchForm,
          page: this.pagination.current,
          row: this.pagination.size
        }

        // 处理allNum
        if (params.allNum && params.allNum.split('-').length === 3) {
          const [floorNum, unitNum, roomNum] = params.allNum.split('-')
          params.floorNum = floorNum.trim()
          params.unitNum = unitNum.trim()
          params.roomNum = roomNum.trim()
        } else {
          params.floorNum = ''
          params.unitNum = ''
          params.roomNum = ''
        }

        const { data, total } = await listPropertyRightRegistration(params)
        // 兼容后端在「无匹配数据」时返回 { code:0, records:0 } 而不带 data 字段的情况。
        this.tableData = Array.isArray(data) ? data : []
        this.pagination.total = total || 0
      } catch (error) {
        console.error('获取列表失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('propertyRightRegistration.page.loadFailed')
        this.$message.error(this.$t('propertyRightRegistration.fetchError'))
      } finally {
        this.loading = false
      }
    },
    async getDictData() {
      try {
        const res = await getDict('property_right_registration', 'state')
        this.states = Array.isArray(res) ? res : []
      } catch (error) {
        console.error('获取字典数据失败:', error)
      }
    },
    async getFloors() {
      try {
        const params = {
          communityId: this.searchForm.communityId,
          page: 1,
          row: 50
        }
        const data = await getFloors(params)
        this.floors = data.apiFloorDataVoList || []
      } catch (error) {
        console.error('获取楼栋数据失败:', error)
      }
    },
    async handleFloorChange(floorId) {
      try {
        const params = {
          floorId,
          communityId: this.searchForm.communityId,
          page: 1,
          row: 50
        }
        const data = await getUnits(params)
        this.units = Array.isArray(data) ? data : []
        this.searchForm.unitId = ''
      } catch (error) {
        console.error('获取单元数据失败:', error)
      }
    },
    handleSearch() {
      this.pagination.current = 1
      this.getList()
    },
    handleReset() {
      this.searchForm = {
        roomId: '',
        allNum: '',
        name: '',
        link: '',
        idCard: '',
        address: '',
        state: '',
        floorId: '',
        unitId: '',
        communityId: getCommunityId()
      }
      this.units = []
      this.handleSearch()
    },
    toggleMoreCondition() {
      this.showMoreCondition = !this.showMoreCondition
    },
    handleAdd() {
      this.$refs.addForm.open()
    },
    handleExamine(row) {
      this.$refs.examineForm.open(row)
    },
    handleEdit(row) {
      this.$refs.editForm.open(row)
    },
    handleDetail(row) {
      this.$router.push({
        path: '/views/room/listPropertyRightRegistrationDetail',
        query: {
          prrId: row.prrId,
          floorNum: row.floorNum,
          unitNum: row.unitNum,
          roomNum: row.roomNum
        }
      })
    },
    handleDelete(row) {
      this.$refs.deleteForm.open(row)
    },
    handleSuccess() {
      this.getList()
    },
    handleSizeChange(val) {
      this.pagination.size = val
      this.pagination.current = 1
      this.getList()
    },
    handleCurrentChange(val) {
      this.pagination.current = val
      this.getList()
    },
    /**
     * 状态语义类型（产权登记）：0 未审核 / 1 审核通过 / 2 审核不通过。
     */
    resolveState(row) {
      if (!row) return 'muted'
      return resolveStatus(row.state, {
        domain: 'propertyRightRegistration',
        text: row.stateName || ''
      }).type
    }
  }
}
</script>

<style lang="scss" scoped>
.property-right-registration-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 更多条件行间距 */
.ds-more-row {
  margin-top: var(--ds-spacing-md);
}

/* 表格次要文字 */
.ds-cell-sub {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}

/* 危险操作文字按钮 */
.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
